/**
 * Context the deposit modal persists RIGHT BEFORE redirecting the payer to the bank (PSE requires the
 * redirect to happen in the same window, so the modal's state is gone when the payer comes back). The return
 * page reads it to show the bank name and amount without waiting for — or depending on — the API.
 * sessionStorage on purpose: it survives the round-trip through the bank in the same tab and nothing else.
 */
export interface PseReturnContext {
  bankName: string;
  grossAmount: string;
  currency: string;
  reference: string;
}

const keyFor = (payinId: string) => `pse-return:${payinId}`;
// Pointer to the payin of the LAST bank redirect — the return page's fallback when it lands without the
// ?payin=<id> query param (both rails normally carry it: PSE via our callback, Botón Bancolombia baked
// into the URL the backend registers with the network).
const LAST_REDIRECT_KEY = "payin-return:last";

export function savePseReturnContext(payinId: string, context: PseReturnContext): void {
  try {
    sessionStorage.setItem(keyFor(payinId), JSON.stringify(context));
    sessionStorage.setItem(LAST_REDIRECT_KEY, payinId);
  } catch {
    // Storage full/blocked — the return page falls back to the API fields.
  }
}

export function loadLastRedirectPayinId(): string | null {
  try {
    return sessionStorage.getItem(LAST_REDIRECT_KEY);
  } catch {
    return null;
  }
}

export function loadPseReturnContext(payinId: string): PseReturnContext | null {
  try {
    const raw = sessionStorage.getItem(keyFor(payinId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PseReturnContext>;
    if (typeof parsed.bankName !== "string" || typeof parsed.grossAmount !== "string") return null;
    return {
      bankName: parsed.bankName,
      grossAmount: parsed.grossAmount,
      currency: typeof parsed.currency === "string" ? parsed.currency : "COP",
      reference: typeof parsed.reference === "string" ? parsed.reference : "",
    };
  } catch {
    return null;
  }
}
